import type {
    SimulationGraph,
    SimulationRunConfig,
    SimulationResultPayload,
    NodeSamples,
    SimulationNode
} from '../../models/simulation';
import type { NodeSampleResult } from '../../models/simulation';
import { topologicalSort } from '../../utils/graph';
import { summarizeSamples } from '../../utils/stats';
import { createRng, sampleDistribution } from './samplers';
import { extractVariableNames, replaceVariableReferences, buildNameToIdMap } from '../../utils/expressionParser';
import {
    compileSafeFormulaExpression,
    initializeCustomUnits,
    math,
    normalizeFormulaEvaluationResult,
    normalizeUnit
} from '../../utils/units';

const evaluateFormulaSamples = (
    expression: string,
    nameToIdMap: Map<string, string>,
    sampleLookup: Map<string, NodeSamples>,
    nodeMap: Map<string, SimulationNode>,
    trials: number,
    currentNodeId?: string,
    currentNodeName?: string,
    startIndex: number = 0
): NodeSamples => {
    // Extract variable names from the expression
    const variableNames = extractVariableNames(expression);

    // Replace ${"name"} with sanitized identifiers
    const { expression: sanitizedExpression, identifierMap } = replaceVariableReferences(
        expression,
        variableNames
    );

    // Build mapping from identifiers to node IDs
    const identifierToNodeId = new Map<string, string>();
    identifierMap.forEach((name, identifier) => {
        const nodeId = nameToIdMap.get(name);
        if (nodeId) {
            identifierToNodeId.set(identifier, nodeId);
        }
    });

    // Check if any referenced nodes have units defined
    const hasUnits = Array.from(identifierToNodeId.values()).some(nodeId => {
        const stored = sampleLookup.get(nodeId);
        if (stored?.unit) {
            return true;
        }
        const node = nodeMap.get(nodeId);
        const nodeUnit = node && node.data.type === 'distribution' ? node.data.unit : undefined;
        return Boolean(normalizeUnit(nodeUnit));
    });

    const values = new Float64Array(trials);
    let resultUnit: string | undefined;

    if (hasUnits) {
        const compiled = compileSafeFormulaExpression(sanitizedExpression);
        // Unit-aware evaluation using math.js
        for (let i = 0; i < trials; i += 1) {
            const mathScope: Record<string, unknown> = {};

            identifierMap.forEach((name, identifier) => {
                const nodeId = nameToIdMap.get(name);
                const sourceSamples = nodeId ? sampleLookup.get(nodeId) : undefined;
                const node = nodeId ? nodeMap.get(nodeId) : undefined;
                const value = sourceSamples ? sourceSamples.values[startIndex + i] : 0;
                const fallbackUnit = node && node.data.type === 'distribution' ? node.data.unit : undefined;
                const unit = normalizeUnit(sourceSamples?.unit ?? fallbackUnit);

                mathScope[identifier] = unit ? math.unit(value, unit) : value;
            });

            try {
                const result = normalizeFormulaEvaluationResult(compiled(mathScope));
                values[i] = result.value;
                const currentUnit = normalizeUnit(result.unit);
                if (currentUnit) {
                    if (resultUnit && resultUnit !== currentUnit) {
                        throw new Error(
                            `Inconsistent result units detected: expected ${resultUnit}, received ${currentUnit}`
                        );
                    }
                    resultUnit = currentUnit;
                }
            } catch (error) {
                const baseError = `Unit validation error in formula: ${(error as Error).message}`;
                const errorWithNode = currentNodeName
                    ? `[${currentNodeName}] ${baseError}`
                    : baseError;
                const err = new Error(errorWithNode);
                (err as any).nodeId = currentNodeId;
                (err as any).nodeName = currentNodeName;
                throw err;
            }
        }
    } else {
        // Standard numeric evaluation (backward compatible)
        const identifiers = Array.from(identifierMap.keys());
        const scope: Record<string, number> = {};
        const compiled = compileSafeFormulaExpression(sanitizedExpression);

        for (let i = 0; i < trials; i += 1) {
            for (let j = 0; j < identifiers.length; j += 1) {
                const identifier = identifiers[j];
                const nodeId = identifierToNodeId.get(identifier);
                const sourceSamples = nodeId ? sampleLookup.get(nodeId) : undefined;
                scope[identifier] = sourceSamples ? sourceSamples.values[startIndex + i] : 0;
            }
            try {
                values[i] = Number(compiled(scope));
            } catch (error) {
                const baseError = `Error evaluating formula: ${(error as Error).message}`;
                const errorWithNode = currentNodeName
                    ? `[${currentNodeName}] ${baseError}`
                    : baseError;
                const err = new Error(errorWithNode);
                (err as any).nodeId = currentNodeId;
                (err as any).nodeName = currentNodeName;
                throw err;
            }
        }
    }

    return {
        values,
        unit: resultUnit
    };
};

const computeSummary = (
    nodeId: string,
    samples: NodeSamples,
    collectSamples: boolean
): NodeSampleResult => ({
    nodeId,
    samples: collectSamples ? samples.values : undefined,
    summary: summarizeSamples(samples.values),
    unit: samples.unit
});


export const runMonteCarloSimulation = (
    graph: SimulationGraph,
    config: SimulationRunConfig,
    onProgress?: (progress: {
        completedTrials: number;
        totalTrials: number;
        partialResults?: Record<string, NodeSampleResult>;
    }) => void
): SimulationResultPayload => {
    const trials = Math.max(1, config.trials);
    const rng = createRng(config.seed ?? Date.now());
    const sampleLookup = new Map<string, NodeSamples>();
    const results: Record<string, NodeSampleResult> = {};
    const collectSamples = config.collectSamples !== false;
    const start = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const BATCH_SIZE = 1000;
    const progressInterval = Math.max(BATCH_SIZE, Math.ceil(trials / 20));
    let nextProgressAt = progressInterval;

    // Initialize custom units in the worker's math.js instance
    if (graph.customUnits && graph.customUnits.length > 0) {
        initializeCustomUnits(graph.customUnits);
    }

    // Build name-to-ID map for resolving variable references
    const nameToIdMap = buildNameToIdMap(graph.nodes);

    // Build node map for unit lookup
    const nodeMap = new Map<string, SimulationNode>();
    graph.nodes.forEach(node => {
        nodeMap.set(node.id, node);
    });

    const sortedNodes = topologicalSort(graph.nodes, graph.edges);

    // Initialize sample storage for all nodes
    sortedNodes.forEach((node) => {
        if (node.data.type === 'distribution') {
            sampleLookup.set(node.id, {
                values: new Float64Array(trials),
                unit: normalizeUnit(node.data.unit)
            });
        } else if (node.data.type === 'formula') {
            sampleLookup.set(node.id, {
                values: new Float64Array(trials),
                unit: undefined // Will be determined during evaluation
            });
        }
    });

    // Process trials in batches
    for (let batchStart = 0; batchStart < trials; batchStart += BATCH_SIZE) {
        const batchEnd = Math.min(batchStart + BATCH_SIZE, trials);
        const batchSize = batchEnd - batchStart;

        sortedNodes.forEach((node) => {
            if (node.data.type === 'distribution') {
                try {
                    // Generate samples for this batch
                    const batchValues = sampleDistribution(node.data.config, batchSize, rng);
                    const samples = sampleLookup.get(node.id)!;
                    // Store batch samples at the correct position
                    samples.values.set(batchValues, batchStart);
                } catch (error) {
                    const baseError = error instanceof Error ? error.message : String(error);
                    const errorWithNode = node.data.name
                        ? `[${node.data.name}] ${baseError}`
                        : baseError;
                    const err = new Error(errorWithNode);
                    (err as any).nodeId = node.id;
                    (err as any).nodeName = node.data.name;
                    throw err;
                }
            }

            if (node.data.type === 'formula') {
                try {
                    // Evaluate formula for this batch
                    const batchSamples = evaluateFormulaSamples(
                        node.data.expression,
                        nameToIdMap,
                        sampleLookup,
                        nodeMap,
                        batchSize,
                        node.id,
                        node.data.name,
                        batchStart
                    );
                    const samples = sampleLookup.get(node.id)!;
                    // Store batch samples at the correct position
                    samples.values.set(batchSamples.values, batchStart);
                    // Update unit if not set yet
                    if (!samples.unit && batchSamples.unit) {
                        samples.unit = batchSamples.unit;
                    }
                } catch (error) {
                    // Re-throw with node information if not already included
                    if (error instanceof Error && !(error as any).nodeId) {
                        const baseError = error.message;
                        const errorWithNode = node.data.name
                            ? `[${node.data.name}] ${baseError}`
                            : baseError;
                        const err = new Error(errorWithNode);
                        (err as any).nodeId = node.id;
                        (err as any).nodeName = node.data.name;
                        throw err;
                    }
                    throw error;
                }
            }
        });

        if (onProgress && batchEnd < trials && batchEnd >= nextProgressAt) {
            onProgress({
                completedTrials: batchEnd,
                totalTrials: trials
            });
            nextProgressAt += progressInterval;
        }
    }

    // Compute final results with all samples
    sortedNodes.forEach((node) => {
        const samples = sampleLookup.get(node.id);
        if (samples) {
            results[node.id] = computeSummary(node.id, samples, collectSamples);
        }
    });

    const end = typeof performance !== 'undefined' ? performance.now() : Date.now();

    return {
        graphId: graph.id,
        results,
        runConfig: {
            ...config,
            collectSamples: true
        },
        durationMs: end - start
    };
};
